import React from 'react';
import {
  alpha,
  Box,
  Divider,
  Drawer,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  TextField,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';
import {
  Close,
  Search,
  Description,
  Dashboard,
  Code,
  Terminal,
  Storage,
  Palette,
  Security,
  Speed,
  Web,
  Chat,
  CloudUpload,
  GitHub,
} from '@mui/icons-material';

interface SidebarItemProps {
  id: string;
  label: string;
  icon: React.ReactNode;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  colors: any;
  isDark: boolean;
  goldColor: string;
  isMobile?: boolean;
  setMobileSidebarOpen?: (open: boolean) => void;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  id,
  label,
  icon,
  activeTab,
  setActiveTab,
  colors,
  isDark,
  goldColor,
  isMobile = false,
  setMobileSidebarOpen = () => {},
}) => {
  const handleItemClick = () => {
    setActiveTab(id);
    if (isMobile) {
      setMobileSidebarOpen(false);
    }
  };

  return (
    <ListItem disablePadding sx={{ mb: 0.5, px: 1 }}>
      <ListItemButton 
        selected={activeTab === id}
        onClick={handleItemClick}
        sx={{ 
          borderRadius: '8px', 
          py: 0.8,
          px: 1.5,
          '&.Mui-selected': { 
            bgcolor: isDark ? alpha(goldColor, 0.15) : alpha(goldColor, 0.1), 
            color: goldColor, 
            '&:hover': { bgcolor: isDark ? alpha(goldColor, 0.2) : alpha(goldColor, 0.15) } 
          },
          '&:hover': { bgcolor: isDark ? alpha(colors.textSecondary, 0.08) : alpha(colors.textSecondary, 0.05) }
        }}
      >
        <ListItemIcon sx={{ 
          minWidth: 32, 
          color: activeTab === id ? goldColor : colors.textSecondary 
        }}> 
          {icon}
        </ListItemIcon>
        <ListItemText 
          primary={label} 
          primaryTypographyProps={{ 
            fontSize: '0.95rem', 
            fontWeight: activeTab === id ? 600 : 500, 
            color: activeTab === id ? goldColor : colors.textSecondary 
          }} 
        />
      </ListItemButton>
    </ListItem>
  );
};

interface DocsSidebarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  colors: any;
  isDark: boolean;
  goldColor: string;
  mobileSidebarOpen: boolean;
  setMobileSidebarOpen: (open: boolean) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

const DocsSidebar: React.FC<DocsSidebarProps> = ({
  activeTab,
  setActiveTab,
  colors,
  isDark,
  goldColor,
  mobileSidebarOpen,
  setMobileSidebarOpen,
  searchQuery,
  setSearchQuery,
}) => {
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const inputStyles = {
    disableUnderline: true,
    style: {
      borderRadius: '8px',
      backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)',
      color: colors.textPrimary,
      paddingTop: '8px',
      paddingBottom: '8px',
      height: 'auto',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
    },
  };

  // Desktop Sidebar
  const desktopSidebar = (
    <Box sx={{ 
      width: 280, 
      bgcolor: colors.sidebarBg, 
      backdropFilter: 'blur(20px)',
      borderRight: `1px solid ${colors.borderColor}`,
      display: 'flex', 
      flexDirection: 'column',
      flexShrink: 0,
      height: 'calc(100vh - 64px)',
      position: 'sticky',
      top: 64,
    }}>
      <Box sx={{ 
        overflowY: 'auto', 
        flex: 1, 
        py: 2, 
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
      }}>
        {/* Search Bar */}
        <Box sx={{ px: 2, mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search documentation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <Box sx={{ mr: 1, color: colors.textSecondary }}>
                  <Search fontSize="small" />
                </Box>
              ),
              ...inputStyles,
            }}
          />
        </Box>

        <Divider sx={{ my: 1, mx: 2, borderColor: colors.borderColor }} />
        
        <Typography variant="caption" fontWeight={800} color={colors.textSecondary} sx={{ 
          px: 2, 
          py: 1, 
          display: 'block', 
          mt: 1, 
          mb: 0.5, 
          textTransform: 'uppercase', 
          fontSize: '0.7rem', 
          letterSpacing: '0.05em' 
        }}>
          Frontend Documentation
        </Typography>

        <SidebarItem 
          id="frontend-overview"
          label="Overview & Vision"
          icon={<Description fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
        />

        <SidebarItem 
          id="frontend-features"
          label="Key Features"
          icon={<Dashboard fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
        />

        <SidebarItem 
          id="frontend-tech-stack"
          label="Technology Stack"
          icon={<Code fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
        />

        <SidebarItem 
          id="frontend-installation"
          label="Installation & Setup"
          icon={<Terminal fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
        />

        <SidebarItem 
          id="frontend-structure"
          label="Project Structure"
          icon={<Storage fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
        />

        <SidebarItem 
          id="frontend-ui-ux"
          label="UI/UX Design System"
          icon={<Palette fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
        />

        <SidebarItem 
          id="frontend-security"
          label="Security Features"
          icon={<Security fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
        />

        <SidebarItem 
          id="frontend-performance"
          label="Performance Optimization"
          icon={<Speed fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
        />

        <Divider sx={{ my: 1, mx: 2, borderColor: colors.borderColor }} />
        
        <Typography variant="caption" fontWeight={800} color={colors.textSecondary} sx={{ 
          px: 2, 
          py: 1, 
          display: 'block', 
          mt: 1, 
          mb: 0.5, 
          textTransform: 'uppercase', 
          fontSize: '0.7rem', 
          letterSpacing: '0.05em' 
        }}>
          Backend Documentation
        </Typography>

        <SidebarItem 
          id="backend-overview"
          label="Backend Overview"
          icon={<Storage fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
        />

        <SidebarItem 
          id="backend-tech-stack"
          label="Backend Tech Stack"
          icon={<Code fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
        />

        <SidebarItem 
          id="backend-installation"
          label="Backend Installation"
          icon={<Terminal fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
        />

        <SidebarItem 
          id="backend-api-endpoints"
          label="API Endpoints"
          icon={<Web fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
        />

        <SidebarItem 
          id="backend-websocket"
          label="WebSocket Integration"
          icon={<Chat fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
        />

        <SidebarItem 
          id="backend-structure"
          label="Backend Structure"
          icon={<Storage fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
        />

        <SidebarItem 
          id="backend-security"
          label="Backend Security"
          icon={<Security fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
        />

        <SidebarItem 
          id="backend-deployment"
          label="Deployment"
          icon={<CloudUpload fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
        />

        <Divider sx={{ my: 1, mx: 2, borderColor: colors.borderColor }} />
        
        <Typography variant="caption" fontWeight={800} color={colors.textSecondary} sx={{ 
          px: 2, 
          py: 1, 
          display: 'block', 
          mt: 1, 
          mb: 0.5, 
          textTransform: 'uppercase', 
          fontSize: '0.7rem', 
          letterSpacing: '0.05em' 
        }}>
          Quick Links
        </Typography>

        <ListItem disablePadding sx={{ mb: 0.5, px: 1 }}>
          <ListItemButton 
            component="a"
            href="https://github.com/Kaiftokare19/WFHCafeDev-Frontend"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ 
              borderRadius: '8px', 
              py: 0.8,
              px: 1.5,
              '&:hover': { bgcolor: isDark ? alpha(colors.textSecondary, 0.08) : alpha(colors.textSecondary, 0.05) }
            }}
          >
            <ListItemIcon sx={{ minWidth: 32, color: colors.textSecondary }}> 
              <GitHub fontSize="small" /> 
            </ListItemIcon>
            <ListItemText 
              primary="Frontend Repo" 
              primaryTypographyProps={{ 
                fontSize: '0.95rem', 
                fontWeight: 500, 
                color: colors.textSecondary 
              }} 
            />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding sx={{ mb: 0.5, px: 1 }}>
          <ListItemButton 
            component="a"
            href="https://github.com/Kaiftokare19/WFHCafeDev-Backend"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ 
              borderRadius: '8px', 
              py: 0.8,
              px: 1.5,
              '&:hover': { bgcolor: isDark ? alpha(colors.textSecondary, 0.08) : alpha(colors.textSecondary, 0.05) }
            }}
          >
            <ListItemIcon sx={{ minWidth: 32, color: colors.textSecondary }}> 
              <GitHub fontSize="small" /> 
            </ListItemIcon>
            <ListItemText 
              primary="Backend Repo" 
              primaryTypographyProps={{ 
                fontSize: '0.95rem', 
                fontWeight: 500, 
                color: colors.textSecondary 
              }} 
            />
          </ListItemButton>
        </ListItem>
      </Box>
    </Box>
  );

  // Mobile Drawer
  const mobileDrawer = (
    <Drawer
      anchor="left"
      open={mobileSidebarOpen}
      onClose={() => setMobileSidebarOpen(false)}
      PaperProps={{
        sx: {
          width: 280,
          bgcolor: colors.sidebarBg,
          backdropFilter: 'blur(20px)',
          borderRight: `1px solid ${colors.borderColor}`,
        }
      }}
    >
      {/* Mobile Drawer Header */}
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: `1px solid ${colors.borderColor}`
      }}>
        <Typography variant="h6" sx={{ 
          fontWeight: 800, 
          color: colors.textPrimary,
          fontFamily: '"Inter", "Segoe UI", sans-serif'
        }}>
          Documentation
        </Typography>
        <IconButton 
          onClick={() => setMobileSidebarOpen(false)}
          sx={{ color: colors.textSecondary }}
        >
          <Close />
        </IconButton>
      </Box>

      <Box sx={{ 
        overflowY: 'auto', 
        flex: 1, 
        py: 2,
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
      }}>
        {/* Search Bar */}
        <Box sx={{ px: 2, mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search documentation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <Box sx={{ mr: 1, color: colors.textSecondary }}>
                  <Search fontSize="small" />
                </Box>
              ),
              ...inputStyles,
            }}
          />
        </Box>

        <Divider sx={{ my: 1, mx: 2, borderColor: colors.borderColor }} />
        
        <Typography variant="caption" fontWeight={800} color={colors.textSecondary} sx={{ 
          px: 2, 
          py: 1, 
          display: 'block', 
          mt: 1, 
          mb: 0.5, 
          textTransform: 'uppercase', 
          fontSize: '0.7rem', 
          letterSpacing: '0.05em' 
        }}>
          Frontend Documentation
        </Typography>

        <SidebarItem 
          id="frontend-overview"
          label="Overview & Vision"
          icon={<Description fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
          isMobile={true}
          setMobileSidebarOpen={setMobileSidebarOpen}
        />

        <SidebarItem 
          id="frontend-features"
          label="Key Features"
          icon={<Dashboard fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
          isMobile={true}
          setMobileSidebarOpen={setMobileSidebarOpen}
        />

        <SidebarItem 
          id="frontend-tech-stack"
          label="Technology Stack"
          icon={<Code fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
          isMobile={true}
          setMobileSidebarOpen={setMobileSidebarOpen}
        />

        <SidebarItem 
          id="frontend-installation"
          label="Installation & Setup"
          icon={<Terminal fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
          isMobile={true}
          setMobileSidebarOpen={setMobileSidebarOpen}
        />

        <SidebarItem 
          id="frontend-structure"
          label="Project Structure"
          icon={<Storage fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
          isMobile={true}
          setMobileSidebarOpen={setMobileSidebarOpen}
        />

        <SidebarItem 
          id="frontend-ui-ux"
          label="UI/UX Design System"
          icon={<Palette fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
          isMobile={true}
          setMobileSidebarOpen={setMobileSidebarOpen}
        />

        <SidebarItem 
          id="frontend-security"
          label="Security Features"
          icon={<Security fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
          isMobile={true}
          setMobileSidebarOpen={setMobileSidebarOpen}
        />

        <SidebarItem 
          id="frontend-performance"
          label="Performance Optimization"
          icon={<Speed fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
          isMobile={true}
          setMobileSidebarOpen={setMobileSidebarOpen}
        />

        <Divider sx={{ my: 1, mx: 2, borderColor: colors.borderColor }} />
        
        <Typography variant="caption" fontWeight={800} color={colors.textSecondary} sx={{ 
          px: 2, 
          py: 1, 
          display: 'block', 
          mt: 1, 
          mb: 0.5, 
          textTransform: 'uppercase', 
          fontSize: '0.7rem', 
          letterSpacing: '0.05em' 
        }}>
          Backend Documentation
        </Typography>

        <SidebarItem 
          id="backend-overview"
          label="Backend Overview"
          icon={<Storage fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
          isMobile={true}
          setMobileSidebarOpen={setMobileSidebarOpen}
        />

        <SidebarItem 
          id="backend-tech-stack"
          label="Backend Tech Stack"
          icon={<Code fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
          isMobile={true}
          setMobileSidebarOpen={setMobileSidebarOpen}
        />

        <SidebarItem 
          id="backend-installation"
          label="Backend Installation"
          icon={<Terminal fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
          isMobile={true}
          setMobileSidebarOpen={setMobileSidebarOpen}
        />

        <SidebarItem 
          id="backend-api-endpoints"
          label="API Endpoints"
          icon={<Web fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
          isMobile={true}
          setMobileSidebarOpen={setMobileSidebarOpen}
        />

        <SidebarItem 
          id="backend-websocket"
          label="WebSocket Integration"
          icon={<Chat fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
          isMobile={true}
          setMobileSidebarOpen={setMobileSidebarOpen}
        />

        <SidebarItem 
          id="backend-structure"
          label="Backend Structure"
          icon={<Storage fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
          isMobile={true}
          setMobileSidebarOpen={setMobileSidebarOpen}
        />

        <SidebarItem 
          id="backend-security"
          label="Backend Security"
          icon={<Security fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
          isMobile={true}
          setMobileSidebarOpen={setMobileSidebarOpen}
        />

        <SidebarItem 
          id="backend-deployment"
          label="Deployment"
          icon={<CloudUpload fontSize="small" />}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          colors={colors}
          isDark={isDark}
          goldColor={goldColor}
          isMobile={true}
          setMobileSidebarOpen={setMobileSidebarOpen}
        />
      </Box>
    </Drawer>
  );

  return (
    <>
      {!isMobile && desktopSidebar}
      {mobileDrawer}
    </>
  );
};

export default DocsSidebar;